import { Component, Vue, Watch } from "vue-property-decorator";
import OrderService from "../../services/order_service";
import NetSuiteService from "../../services/netsuite_service";
import QuoteDraftService from "../../services/quote_draft_service";
import {
  DriftReport,
  QuoteDraft,
  QuoteDraftStatus,
  SalesOrderSummary,
  toLineItem
} from "../../models/quote_draft/quote_draft";
import { OrderStatus } from "../../models/order/helpers";
import { AuthError, GenericError } from "../../services/error_service";
import { NotificationService } from "../../services/notification_service";
import { namespace } from "vuex-class";
import { StoreModule } from "../../store/types";
import { OrderActions, OrderGetters } from "../../store/modules/order/types";
import { GlobalActions, GlobalGetters } from "../../store/modules/global/types";
import { UserGetters } from "../../store/modules/user/types";
import {
  priceLevelMultiplier,
  positivePriceLevelId,
  formatSalesOrderDate
} from "../../utility/helpers";
import { LineItem } from "../../models/order/order";
import { composeLineNotes } from "../../models/order/line_notes";
import { stripQuoteNotes, multiplierWarning } from "../../models/order/quote_notes";
import { ProductType } from "../../models/products/product";
import { setupGroupAt } from "../../utility/line_setup";
import { sharedDesignAdjusted } from "../../utility/shared_design";
// A separate statement from the helpers import above on purpose: FUPOF-200
// edits that one, and the two branches merge in -187.
import { makeTitleCase } from "../../utility/helpers";
import { Order } from "../../models/order/order";
import {
  CreateNetSuiteQuoteLineItem,
  CreateNetSuiteQuotePayload,
  CreateNetSuiteQuoteShippingAddress,
  DuplicateCustomerMatch,
  FoundNetSuiteItemBySku,
  NetSuiteQuote,
  PartialNetSuiteCustomer,
  PriceLevel
} from "../../models/netsuite/netsuite";
import { User } from "../../models/user/user";
import { Roles } from "../../models/user/roles";
import { NetSuiteQuoteStatus } from "../../models/netsuite/helpers";
import { PAGE_SIZE, clampPage, pageSlice } from "../../utility/pagination";

@Component
export class OrderMixin extends Vue {
  @(namespace(StoreModule.Order).Getter(OrderGetters.GetOrderItems))
  items!: LineItem[];
  @(namespace(StoreModule.User).Getter(UserGetters.GetUser))
  user!: User;
  @(namespace(StoreModule.Global).Getter(GlobalGetters.GetMultiplier))
  quoteMultiplierLevel!: PriceLevel | null;
  @(namespace(StoreModule.Global).Getter(GlobalGetters.GetPriceLevels))
  storePriceLevels!: PriceLevel[];
  @(namespace(StoreModule.Global).Action(GlobalActions.AddPriceLevels))
  addPriceLevelsAction!: (value: PriceLevel[]) => Promise<void>;
  @(namespace(StoreModule.Global).Action(GlobalActions.AddMultiplier))
  addMultiplierAction!: (value: PriceLevel | null) => Promise<void>;
  @(namespace(StoreModule.Order).Action(OrderActions.ReplaceLineItems))
  replaceLineItemsAction!: (value: LineItem[]) => Promise<void>;
  @(namespace(StoreModule.Order).Action(OrderActions.ApplyQuoteMultiplier))
  applyQuoteMultiplierAction!: (value: number) => Promise<void>;
  @(namespace(StoreModule.Order).Getter(OrderGetters.GetOrder))
  mixinStoreOrder!: Order;
  @(namespace(StoreModule.Order).Action(OrderActions.SaveDraft))
  saveDraftAction!: () => Promise<QuoteDraft>;
  @(namespace(StoreModule.Order).Action(OrderActions.LoadDraft))
  loadDraftAction!: (value: number | QuoteDraft) => Promise<QuoteDraft>;
  @(namespace(StoreModule.Order).Action(OrderActions.DeleteDraft))
  deleteDraftAction!: (id: number) => Promise<void>;
  @(namespace(StoreModule.Order).Action(OrderActions.ReloadFromNs))
  reloadFromNsAction!: () => Promise<DriftReport>;
  @(namespace(StoreModule.Order).Action(OrderActions.MarkAsDuplicate))
  markAsDuplicateAction!: () => Promise<void>;
  @(namespace(StoreModule.Order).Action(OrderActions.MarkAsReorder))
  markAsReorderAction!: () => Promise<void>;
  @(namespace(StoreModule.Order).Action(OrderActions.ApplySubmittedDraft))
  applySubmittedDraftAction!: (draft: QuoteDraft) => Promise<void>;
  @(namespace(StoreModule.Order).Action(OrderActions.SetNetSuiteTranId))
  setNetSuiteTranIdAction!: (tranId: string | null) => Promise<void>;
  @(namespace(StoreModule.Global).Getter(GlobalGetters.GetRole))
  mixinUserRole!: string;
  @(namespace(StoreModule.Global).Getter(GlobalGetters.GetCustomers))
  mixinStoreCustomers!: PartialNetSuiteCustomer[];
  @(namespace(StoreModule.Global).Action(GlobalActions.AddCustomers))
  addCustomersAction!: (value: PartialNetSuiteCustomer[]) => Promise<void>;
  @(namespace(StoreModule.Order).Action(OrderActions.AssignCustomer))
  assignCustomerAction!: (value: {
    customer_id: string;
    customer_name: string;
    customer_netsuite_id: number | undefined;
    customer_netsuite_contact_id: number | undefined;
    customer_netsuite_payment_terms_id: number | undefined;
    customer_netsuite_price_level_id: number | undefined;
    customer_netsuite_price_level_discount: number | undefined;
    order_id: string | null;
  }) => Promise<void>;

  /**
   * FUPOF-191: point this quote at a NetSuite customer that already exists,
   * instead of creating a second record for it.
   *
   * The duplicate check reports a match by NetSuite id alone, but assigning a
   * customer needs its price level and payment terms, which live in the loaded
   * customer list. A customer the rep created minutes ago is in NetSuite but not
   * yet in that cached list, so a miss is worth one forced refresh before giving
   * up — that near-miss is the most common duplicate there is.
   *
   * A customer still missing after the refresh belongs to someone else's book.
   * Assigning it anyway would be worse than useless: App drops any order whose
   * customer isn't in the rep's list, taking the cart with it. Say so instead.
   *
   * Returns whether the quote was switched.
   */
  protected async switchQuoteToExistingCustomer(
    match: DuplicateCustomerMatch
  ): Promise<boolean> {
    let customer = (this.mixinStoreCustomers ?? []).find(
      c => c.customer_id === match.id
    );
    if (!customer) {
      const refreshed = await this.netSuiteService.getCustomersForRole(
        this.mixinUserRole
      );
      if (refreshed.length) {
        await this.addCustomersAction(refreshed);
        customer = refreshed.find(c => c.customer_id === match.id);
      }
    }
    if (!customer) {
      NotificationService.show(
        `${match.company_name} is in NetSuite but not in your customer list${
          match.sales_rep ? ` — its sales rep is ${match.sales_rep}` : ""
        }. Ask an admin to assign it to you, then start the quote from the customer list.`,
        "warning"
      );
      return false;
    }
    const existing = customer;
    await this.assignCustomerAction({
      customer_id: existing.customer_id.toString(),
      customer_name: existing.company_name,
      customer_netsuite_id: existing.customer_id,
      customer_netsuite_contact_id: undefined,
      customer_netsuite_payment_terms_id:
        existing.payment_terms_id || undefined,
      customer_netsuite_price_level_id: existing.price_level_id || undefined,
      customer_netsuite_price_level_discount:
        existing.price_level_discount ?? undefined,
      order_id: null
    });
    // Base Price is the fallback for a customer with no level AND for one whose
    // level is not submittable (Custom, filtered out of `storePriceLevels`) — a miss
    // on the id lookup must not leave the quote with no multiplier at all.
    const priceLevel =
      (existing.price_level_id
        ? this.storePriceLevels.find(l => l.id === existing.price_level_id)
        : null) ??
      this.storePriceLevels.find(l => l.name === "Base Price") ??
      null;
    await this.addMultiplierAction(priceLevel);
    // The quote moved to a different customer, so their level applies to every
    // line — written here rather than left to the cart's mount-time stamp,
    // which no longer overwrites a deliberately mixed cart. See
    // `CustomerMixin.saveCustomerToStore` for the same pairing.
    if (priceLevel) {
      await this.applyQuoteMultiplierAction(
        priceLevelMultiplier(priceLevel.discountpct)
      );
    }
    return true;
  }

  protected productType = ProductType;
  protected action: null | "edit" | "dupe" | "reorder" = null;
  protected showModal = false;

  /**
   * Title for the clear-cart confirm modal, matched to the pending action and
   * suffixed with the quote's document number (e.g. "Edit Quote QUO2454") so
   * the rep can see exactly which quote the action targets.
   */
  protected get clearAllModalTitle(): string {
    const base =
      this.action === "reorder"
        ? "Reorder from Sales Order"
        : this.action === "dupe"
        ? "Duplicate Quote"
        : "Edit Quote";
    const label = this.pendingQuoteLabel;
    return label ? `${base} ${label}` : base;
  }

  /**
   * The value shown in the quotes table's "Quote #" column: a NetSuite quote's
   * `_id` is its QUO#### document number, a draft's is its id. Surfaced in the
   * edit/duplicate confirm modal and loader so the rep can see exactly which
   * quote the action targets — same slice the table uses so the two match.
   */
  protected quoteDisplayNumber(order: Order | null): string {
    return order && order._id ? String(order._id).slice(-7) : "";
  }

  /**
   * The pending action's display number for the confirm modal. A reorder is
   * identified by the SALES ORDER it copies, not by a quote number — the row it
   * starts from may be a Sales Order with no quote behind it at all (FUPOF-200).
   */
  protected get pendingQuoteLabel(): string {
    if (this.action === "reorder") {
      return (
        this.pendingSalesOrder?.sales_order_number ||
        this.pendingOrder?.netsuite_sales_order_number ||
        ""
      );
    }
    return this.quoteDisplayNumber(this.pendingOrder);
  }
  protected id = "";
  protected status: OrderStatus | null = null;
  protected orderStatus = OrderStatus;
  protected orderService = new OrderService();
  protected netSuiteService = new NetSuiteService();
  protected quoteDraftService = new QuoteDraftService();
  protected netSuiteQuotes: Order[] = [];
  protected orders: Order[] = [];
  protected drafts: QuoteDraft[] = [];
  protected pendingOrder: Order | null = null;
  /**
   * FUPOF-200: the Sales Order a pending reorder copies, when the action was
   * started from a Sales Order row rather than a quote row. Kept in its own
   * field so `pendingOrder` stays honestly typed as an Order — a Sales Order is
   * not one, and coercing it into an Order-shaped stub is exactly the shortcut
   * SalesOrderSummary exists to avoid.
   */
  protected pendingSalesOrder: SalesOrderSummary | null = null;
  protected search = "";
  protected activeTab: string = NetSuiteQuoteStatus.Open;
  protected draftsTabKey = "Drafts";
  /** 1-based page of `filteredOrders` on screen; see `pagedOrders`. */
  protected quotesPage = 1;
  protected quotesPerPage = PAGE_SIZE;

  protected get sortedOrders(): Order[] {
    return this.orders
      .slice()
      .sort((a, b) => {
        //@ts-ignore
        return (new Date(b.created_at) as any) - (new Date(a.created_at) as any);
      })
      .filter(o => o.status !== OrderStatus.Converted);
  }

  protected get tabCounts(): Record<string, number> {
    const orders = this.sortedOrders;
    const countNs = (status: NetSuiteQuoteStatus): number =>
      orders.filter(
        o => o.netsuite_id && this.effectiveStatus(o) === status
      ).length;
    return {
      [this.draftsTabKey]: orders.filter(o => this.isDraftRow(o)).length,
      [NetSuiteQuoteStatus.Open]: countNs(NetSuiteQuoteStatus.Open),
      [NetSuiteQuoteStatus.PendingSalesOrder]: countNs(
        NetSuiteQuoteStatus.PendingSalesOrder
      ),
      [NetSuiteQuoteStatus.Processed]: countNs(NetSuiteQuoteStatus.Processed),
      [NetSuiteQuoteStatus.Expired]: countNs(NetSuiteQuoteStatus.Expired),
      Legacy: orders.filter(o => !o.netsuite_id && !this.isDraftRow(o)).length
    };
  }

  /**
   * The tab a NetSuite quote row belongs in. NetSuite itself only reports
   * Open/Processed/Expired, so the "Pending Sales Order" bucket is overlaid from
   * the PSQL draft: when a rep has requested SO conversion the draft is
   * `pending_sales_order` while its NS estimate is still Open. Falls back to the
   * NS status string otherwise.
   */
  protected effectiveStatus(order: Order): string {
    const draft = this.findDraftForOrder(order);
    if (draft?.status === QuoteDraftStatus.PendingSalesOrder) {
      return NetSuiteQuoteStatus.PendingSalesOrder;
    }
    return ((order as unknown) as NetSuiteQuote).status;
  }

  protected get filteredOrders(): Order[] {
    let orders: Order[];

    if (this.activeTab === this.draftsTabKey) {
      orders = this.sortedOrders.filter(o => this.isDraftRow(o));
    } else if (this.activeTab === "Legacy") {
      orders = this.sortedOrders.filter(
        o => !o.netsuite_id && !this.isDraftRow(o)
      );
    } else {
      orders = this.sortedOrders.filter(
        o => o.netsuite_id && this.effectiveStatus(o) === this.activeTab
      );
    }

    if (this.search !== "") {
      const term = this.search.toLowerCase();
      // Match customer name, quote name, or the Quote # as shown in the table
      // (`_id` — its last 7 chars are rendered, so a full-id contains-match
      // covers what the user sees and typing the whole doc number too). A single
      // OR filter avoids the duplicate rows the old byCustomer.concat(byQuoteName)
      // produced when a row matched more than one field. The Processed tab also
      // renders a Sales Order #, so match that column there too.
      const includeSalesOrder =
        this.activeTab === NetSuiteQuoteStatus.Processed;
      return (orders as any[]).filter(item => {
        const customerName = item.customer_id?.customer_name?.toLowerCase() ?? "";
        const quoteName = item.contact_details?.quote_name?.toLowerCase() ?? "";
        const quoteNumber = item._id != null ? String(item._id).toLowerCase() : "";
        const salesOrderNumber =
          includeSalesOrder && item.netsuite_sales_order_number != null
            ? String(item.netsuite_sales_order_number).toLowerCase()
            : "";
        return (
          customerName.indexOf(term) !== -1 ||
          quoteName.indexOf(term) !== -1 ||
          quoteNumber.indexOf(term) !== -1 ||
          (salesOrderNumber !== "" && salesOrderNumber.indexOf(term) !== -1)
        );
      });
    }

    return orders;
  }

  /**
   * One page of `filteredOrders`. Quotes are fetched whole (about 1,700 for an
   * admin) and paged here in the browser, so a page change is instant. The page
   * is clamped rather than trusted, so deleting the last row on the last page
   * lands on the page before it instead of an empty table.
   */
  protected get pagedOrders(): Order[] {
    return pageSlice(this.filteredOrders, this.quotesPage, this.quotesPerPage);
  }

  /** `quotesPage` as displayed, after the same clamp `pagedOrders` applies. */
  protected get quotesPageShown(): number {
    return clampPage(
      this.quotesPage,
      this.filteredOrders.length,
      this.quotesPerPage
    );
  }

  /** A different tab or search is a different list; start it from the top. */
  @Watch("activeTab")
  @Watch("search")
  protected resetQuotesPage(): void {
    this.quotesPage = 1;
  }

  // ToDo: FNSI-101 - Needs rewrite. Temporary logic since NetSuiteQuoteLineItems have not yet been converted to Order LineItem objects.
  protected getItemsTally(items: LineItem[]): number {
    let num = 0;
    for (const item of items) {
      const netSuiteItemQty = (item as any).quantity;
      if (netSuiteItemQty && !isNaN(netSuiteItemQty)) {
        num += Math.abs(netSuiteItemQty);
      } else if (item.qty && !isNaN(item.qty)) {
        num += item.qty;
      }
    }
    return num;
  }

  protected getTotal(items: LineItem[], costDetails: any): number {
    if (costDetails && costDetails.line_item_total) {
      return costDetails.line_item_total;
    }
    let num = 0;
    for (const item of items) {
      num = num + item.qty * item.unit_price * item.multiplier;
    }
    return num;
  }

  /**
   * The NetSuite production-notes string for a line: the rep's free text plus
   * the configurator's per-field notes, flattened. Submit is the only thing
   * that composes — the draft stores the two halves apart (see
   * `composeLineNotes`), which is why the API's reload sees them as drifted.
   *
   * `configSource` opts the line into the spelled-out Configuration block and
   * supplies the structured line to build it from — see `composeLineNotes`.
   */
  protected stringifyItemNotes(
    item: LineItem,
    configSource?: LineItem | null
  ): string {
    return composeLineNotes(item, configSource);
  }

  protected isNetSuiteQuote(order: Order): boolean {
    return !!order.netsuite_id;
  }

  // FUPOF-200: the implementation moved to utility/helpers so SalesOrderTable
  // can format dates without inheriting this whole mixin. Kept as a method so
  // every existing template call site (OrderList, CustomerOrders) is unchanged.
  protected formatSalesOrderDate(date?: string | null): string {
    return formatSalesOrderDate(date);
  }

  protected async confirm(): Promise<void> {
    const order = this.pendingOrder;
    const salesOrder = this.pendingSalesOrder;
    const action = this.action;
    this.close();
    // FUPOF-200: reorder is handled first and returns. This used to be a plain
    // if/else that collapsed everything not "edit" into duplicateQuote, so a
    // reorder falling through would silently duplicate the QUOTE instead of
    // building a new one from the sales order.
    if (action === "reorder") {
      // Two entry points reach here: a Sales Order row (SalesOrderList), and a
      // Processed quote row that carries the id of the order it converted into.
      if (salesOrder) {
        await this.reorderFromSalesOrder(
          salesOrder.sales_order_id,
          salesOrder.sales_order_number
        );
      } else if (order?.netsuite_sales_order_id) {
        await this.reorderFromSalesOrder(
          order.netsuite_sales_order_id,
          order.netsuite_sales_order_number
        );
      }
      return;
    }
    if (!order) return;
    if (action === "edit") {
      await this.openQuoteForEdit(order);
    } else {
      await this.duplicateQuote(order);
    }
  }

  /**
   * FUPOF-191: converts the user's PSQL drafts (status=draft only — submitted
   * ones surface through their NS status tabs) into Order rows for the table.
   */
  protected convertDraftsToOrders(drafts: QuoteDraft[]): Order[] {
    return drafts
      .filter(d => d.status === QuoteDraftStatus.Draft)
      .map(d => {
        const header = d.header_payload ?? ({} as QuoteDraft["header_payload"]);
        return ({
          _id: String(d.id),
          draft_id: d.id,
          draft_status: d.status,
          created_at: d.created_at,
          customer_id: header.customer_name
            ? { customer_name: header.customer_name }
            : undefined,
          contact_details: {
            quote_name: d.title,
            company_name:
              header.contact_details?.company_name ?? header.customer_name ?? ""
          },
          customer_netsuite_price_level_discount:
            header.customer_netsuite_price_level_discount ?? undefined,
          cost_details: header.cost_details ?? undefined,
          // Creator id + name ride along so the list can gate delete to the
          // creator and label a teammate's draft (drafts are team-shared).
          user_id: d.user_id,
          owner_name: d.owner_name,
          items: (d.lines ?? []).map(toLineItem)
        } as unknown) as Order;
      });
  }

  /**
   * The multiplier shown in the quotes tables (OrderList, CustomerOrders).
   *
   * FUPOF-191: the lines are the authority, so they are read first. The
   * header's `customer_netsuite_price_level_discount` is only the customer's
   * NetSuite default — checking it first listed a quote saved at x0.35 as the
   * customer's x0.60, before the rep had even opened it.
   *
   * 11/3/2023_FUPOF-137: Quotes are to have the same multiplier for all items.
   */
  protected getMultiplier(
    items: LineItem[],
    price_level_discount?: number
  ): number {
    const lineMultiplier = items?.length ? items[0].multiplier : undefined;
    if (lineMultiplier) return parseFloat(lineMultiplier.toFixed(4));
    return priceLevelMultiplier(price_level_discount);
  }

  protected isDraftRow(order: Order): boolean {
    return !!order.draft_id && order.draft_status === QuoteDraftStatus.Draft;
  }

  /**
   * A draft is deletable only by its creator (or an admin) — teammates may edit
   * and submit a shared draft but not remove it. Mirrors the API's QuotePolicy,
   * which is the authority; this only decides whether to show the button.
   */
  protected canDeleteQuote(order: Order): boolean {
    if (!this.isDraftRow(order)) return false;
    return order.user_id === this.user?.id || this.user?.role === Roles.Admin;
  }

  /**
   * A draft owned by a teammate (someone other than the current user). Used to
   * surface a "who created it" label on shared rows; own drafts stay unlabelled.
   */
  protected isTeammateDraft(order: Order): boolean {
    return (
      this.isDraftRow(order) &&
      !!order.owner_name &&
      order.user_id !== this.user?.id
    );
  }

  /** Only drafts and Open NetSuite quotes may be edited. Once a Sales Order has
   * been requested (Pending Sales Order) the quote is locked; Processed and
   * Expired quotes can still be duplicated or downloaded. effectiveStatus keeps a
   * pending quote — whose NS estimate is still Open — out of the editable set. */
  protected canEditQuote(order: Order): boolean {
    return (
      this.isDraftRow(order) ||
      (!!order.netsuite_id &&
        this.effectiveStatus(order) === NetSuiteQuoteStatus.Open)
    );
  }

  /**
   * Re-read the drafts list into `this.drafts` right before a decision that
   * depends on it being complete. Returns whether the read succeeded — the
   * caller needs to tell "no draft exists" apart from "we couldn't find out",
   * because only the first of those justifies adopting from NetSuite.
   *
   * Both list-bearing views load drafts once on mount and swallow the failure
   * (`prepareDrafts` logs a warning and leaves the list empty), which is fine
   * for rendering a table and not fine for choosing between loading a draft and
   * replacing it with a NetSuite import.
   */
  protected async refreshDraftsForEdit(): Promise<boolean> {
    try {
      this.drafts = await this.quoteDraftService.listQuotes();
      return true;
    } catch (err) {
      console.warn("Could not refresh quote drafts before editing:", err);
      return false;
    }
  }

  /** The PSQL draft backing an NS quote row, if this user owns one. */
  protected findDraftForOrder(order: Order): QuoteDraft | undefined {
    if (order.draft_id) {
      return this.drafts.find(d => d.id === order.draft_id);
    }
    if (order.netsuite_id) {
      return this.drafts.find(d => d.netsuite_quote_id === order.netsuite_id);
    }
    return undefined;
  }

  /**
   * Dev/test affordance: when VUE_APP_DRIFT_TEST_MODE is "true", plain drafts
   * (no netsuite_quote_id) also trigger the NetSuite reload, so the
   * DriftResolver can be exercised without a NetSuite-linked quote. Mirrors the
   * Laravel-side .env test toggle. Unset in production, so the normal gate
   * (netsuite_quote_id present) applies.
   */
  protected get driftTestMode(): boolean {
    return process.env.VUE_APP_DRIFT_TEST_MODE === "true";
  }

  /**
   * FUPOF-191: restores the quote-wide price level from a quote that was just
   * hydrated into the store, and pins any line that doesn't sit on it.
   *
   * A saved quote's multiplier lives on its lines — PSQL persists it per line,
   * and the header carries only `customer_netsuite_price_level_id`, the
   * customer's NetSuite default. The global level, meanwhile, is whatever the
   * last cart left behind (often null, or another customer's default). Nothing
   * reconciled the two, so `Cart.loadCartPriceLevels` and
   * `EditMultiplier.created` both fell back to the customer default on mount
   * and re-stamped every line with it: a quote saved at x0.35 reopened at the
   * customer's x0.60, silently, with totals recalculated to match.
   *
   * Resolving the level here — from the lines, which are the authority —
   * settles it before the cart mounts, so the re-stamp becomes a no-op and a
   * line added to the reopened quote inherits the same level. A quote whose
   * lines disagree, or whose multiplier matches no published level, has no
   * single level to restore: those lines are pinned instead, and the fallback
   * to the customer default stands as the default for *new* lines only.
   * `EditMultiplier` (an explicit rep action) still forces every line onto one
   * level and clears the pins.
   *
   * Takes the draft rather than reading the store alone: `toLineItem` defaults
   * a missing multiplier to 1, which is indistinguishable from a line genuinely
   * quoted at Base Price. A payload that carries no multiplier at all has
   * nothing to restore, so it leaves the pre-existing customer-default behavior
   * in place instead of pinning the quote at x1.00.
   */
  protected async syncQuoteMultiplierFromLines(
    draft: QuoteDraft
  ): Promise<void> {
    const items = (this.items ?? []) as LineItem[];
    const lines = draft?.lines ?? [];
    if (!items.length || lines.length !== items.length) return;
    if (lines.some(line => line.multiplier == null)) return;
    if (!this.storePriceLevels?.length) {
      try {
        await this.addPriceLevelsAction(
          await this.netSuiteService.getPriceLevels()
        );
      } catch (err) {
        // Non-fatal: without the list nothing resolves, so every line pins and
        // the quote keeps the multipliers it was saved with.
        console.warn("Price levels unavailable:", err);
      }
    }
    const levels = this.storePriceLevels ?? [];
    const values = items.map(item => (item.multiplier ?? 1).toFixed(4));
    const uniform = values.every(value => value === values[0])
      ? values[0]
      : null;
    const level = uniform
      ? levels.find(
          l => priceLevelMultiplier(l.discountpct).toFixed(4) === uniform
        ) ?? null
      : null;
    await this.addMultiplierAction(level);
    if (level) return;
    await this.replaceLineItemsAction(
      items.map(item => ({ ...item, ns_multiplier: true }))
    );
  }

  /**
   * FUPOF-191 edit flow: resolve (or adopt) the PSQL draft, hydrate the
   * store, run the NS drift check for submitted quotes, then open the cart.
   */
  protected async openQuoteForEdit(order: Order): Promise<void> {
    const label = this.quoteDisplayNumber(order);
    const loader = this.$loading.show(
      { opacity: 1 },
      {
        before: this.$createElement(
          "h1",
          `Editing Quote${label ? ` ${label}` : ""}`
        )
      }
    );
    try {
      let draft = this.findDraftForOrder(order);
      let adopted = false;
      if (!draft && order.netsuite_id) {
        // Adoption is a one-way downgrade: every NetSuite line comes back
        // best-effort SKU-parsed, and the ones the parser can't touch — every
        // TEMP-CUST-ITEM custom line among them — come back `ns_locked`, i.e.
        // configuration read-only with an "Added in NetSuite" pill. The adopted
        // header_payload is the customer block alone, so the rep's contact,
        // shipping, billing and payment entries are not in it either.
        //
        // That is the right outcome for a genuinely NetSuite-only quote and the
        // wrong one for a quote this app authored whose draft simply wasn't in
        // `this.drafts` — a drafts request that failed on page load leaves the
        // list empty and silently sends every edit down this path. So re-read
        // the list first and only adopt once a fresh copy still has no match.
        const fresh = await this.refreshDraftsForEdit();
        draft = this.findDraftForOrder(order);
        if (!draft) {
          if (!fresh) {
            throw new Error(
              "Your saved quotes could not be loaded, so this quote can't be opened for editing yet. Please refresh and try again."
            );
          }
          draft = await this.quoteDraftService.adoptQuote(order.netsuite_id);
          adopted = draft.netsuite_quote_id === order.netsuite_id;
        }
      }
      if (!draft) {
        throw new Error("This quote cannot be edited.");
      }
      const loaded = await this.loadDraftAction(draft.id);
      // The lines carry the quote's multiplier; restore the quote-wide level
      // from them before the cart mounts and re-asserts one of its own.
      await this.syncQuoteMultiplierFromLines(loaded);
      // The NS document number (e.g. "QUO2400") lives on the quote row, not the
      // PSQL draft — carry it into the store so the edit-mode banner can show it.
      if (loaded.netsuite_quote_id && order._id) {
        await this.setNetSuiteTranIdAction(order._id);
      }
      // Only a NetSuite-linked quote can have drifted from NetSuite. In
      // dev/test, VUE_APP_DRIFT_TEST_MODE relaxes this so plain drafts also
      // trigger the reload — see driftTestMode.
      if (loaded.netsuite_quote_id || this.driftTestMode) {
        try {
          await this.reloadFromNsAction();
        } catch (err) {
          // Logged as well as shown: a failed reload is the one way the
          // DriftResolver silently never appears, and the toast alone gave
          // nothing to diagnose it with.
          console.error("[openQuoteForEdit] NetSuite reload failed:", err);
          NotificationService.show(
            "Could not check NetSuite for changes to this quote. You may be viewing outdated line details.",
            "warning"
          );
        }
      } else if (order.netsuite_id) {
        // The row came from NetSuite but its draft carries no link back, so the
        // drift check above was skipped entirely and changes made in NetSuite
        // will not be offered for review. This is the state a submit leaves
        // behind when NetSuite created the estimate but the response never
        // landed — say so rather than quietly showing stale lines.
        console.warn(
          `[openQuoteForEdit] Quote ${order._id ??
            order.netsuite_id} has no netsuite_quote_id on its saved draft; skipping the NetSuite drift check.`
        );
        NotificationService.show(
          "This quote isn't linked to its NetSuite record, so changes made in NetSuite can't be checked. Saving will create a new NetSuite quote.",
          "warning"
        );
      }
      if (adopted) {
        // Adoption rebuilt this quote from NetSuite rather than from a saved
        // draft: custom lines come back read-only and the header is empty.
        // Unexplained, that reads as the app having lost the rep's work.
        NotificationService.show(
          "This quote was imported from NetSuite because no saved draft was found. Custom lines are read-only, and the quote's contact, shipping and billing details need to be re-entered.",
          "warning"
        );
      }
      loader.hide();
      this.$router.push("/cart");
    } catch (err) {
      loader.hide();
      GenericError.popup(err.message);
    }
  }

  /**
   * FUPOF-191 duplicate flow: hydrate the store from the source, then strip
   * draft/NS identity so the cart holds a fresh unsaved copy.
   *
   * A duplicate must never persist the source: for an NS-only quote we hydrate
   * from the read-only /adopt/preview payload directly (its id is null — there
   * is no row to load) instead of adopting it into PSQL. The first PSQL row a
   * duplicate produces is the one Save Draft writes on the copy. An existing
   * PSQL draft still loads by id so `loadDraft` fetches its full line set.
   */
  protected async duplicateQuote(order: Order): Promise<void> {
    const label = this.quoteDisplayNumber(order);
    const loader = this.$loading.show(
      { opacity: 1 },
      {
        before: this.$createElement(
          "h1",
          `Duplicating Quote${label ? ` ${label}` : ""}`
        )
      }
    );
    try {
      const existing = this.findDraftForOrder(order);
      let loaded: QuoteDraft;
      if (existing) {
        loaded = await this.loadDraftAction(existing.id);
      } else if (order.netsuite_id) {
        const preview = await this.quoteDraftService.previewAdoptQuote(
          order.netsuite_id
        );
        loaded = await this.loadDraftAction(preview);
      } else {
        throw new Error("This quote cannot be duplicated.");
      }
      // A copy is quoted at the source's level, not the customer's default.
      await this.syncQuoteMultiplierFromLines(loaded);
      await this.markAsDuplicateAction();
      loader.hide();
      this.$router.push("/cart");
    } catch (err) {
      loader.hide();
      GenericError.popup(err.message);
    }
  }

  /**
   * FUPOF-200 reorder flow: build an unsaved draft from a NetSuite Sales Order,
   * hydrate the store straight from that transient payload (its id is null —
   * there is no row to load, and none is written until the rep's Save Draft),
   * then stamp it as a reorder so the ship-to carried over from the order
   * survives checkout.
   *
   * `loadDraftAction` takes a QuoteDraft object as readily as an id, so the
   * preview hydrates with no second round trip — the same trick duplicateQuote
   * uses — and it warms the customer prefetch for checkout on the way through.
   */
  protected async reorderFromSalesOrder(
    salesOrderId: number,
    salesOrderNumber?: string | null
  ): Promise<void> {
    const label = salesOrderNumber || `#${salesOrderId}`;
    const loader = this.$loading.show(
      { opacity: 1 },
      { before: this.$createElement("h1", `Building Quote from ${label}`) }
    );
    try {
      const preview = await this.quoteDraftService.previewDuplicateSalesOrder(
        salesOrderId
      );
      const loaded = await this.loadDraftAction(preview.quote);
      // A reorder is quoted at the level the API repriced it to, not the
      // customer's default — same reason duplicateQuote syncs from the lines.
      await this.syncQuoteMultiplierFromLines(loaded);
      await this.markAsReorderAction();
      loader.hide();
      const stale = preview.stale_lines?.length ?? 0;
      if (stale) {
        NotificationService.show(
          `${stale} line${stale === 1 ? "" : "s"} from ${label} need${
            stale === 1 ? "s" : ""
          } review before submitting.`,
          "warning"
        );
      }
      this.$router.push("/cart");
    } catch (err) {
      loader.hide();
      GenericError.popup(err.message);
    }
  }

  /**
   * FUPOF-200: the Sales Order equivalent of `requestQuoteAction` — opens the
   * same clear-cart confirmation (decision D6) before a reorder replaces the
   * store, and skips it when the cart holds nothing to lose.
   */
  protected requestSalesOrderAction(salesOrder: SalesOrderSummary): void {
    this.pendingSalesOrder = salesOrder;
    // A Sales Order row has no quote behind it; make sure a stale pendingOrder
    // can't stand in for one in confirm() or the modal title.
    this.pendingOrder = null;
    this.action = "reorder";
    this.id = String(salesOrder.sales_order_id);
    this.status = null;
    if (!this.items || this.items.length === 0) {
      this.confirm();
      return;
    }
    this.showModal = true;
  }

  protected async download(id: string): Promise<void> {
    try {
      NotificationService.show(
        `Downloading legacy quote #${id.slice(-7)}...`,
        "warning"
      );
      const blobData = await this.orderService.downloadOrderInvoice(id);
      this.forceDownload(
        blobData,
        `Frankford Umbrellas Submitted Quote #${id.slice(-7)}`
      );
      NotificationService.show(
        `Legacy quote #${id.slice(-7)} downloaded.`,
        "success"
      );
    } catch (err) {
      if (err instanceof AuthError) {
        AuthError.logout();
      }
      GenericError.popup(err.message);
    }
  }

  protected async downloadQuote(id: string): Promise<void> {
    try {
      NotificationService.show(
        `Downloading legacy quote #${id.slice(-7)}...`,
        "warning"
      );
      const blobData = await this.orderService.downloadQuoteInvoice(id);
      this.forceDownload(
        blobData,
        `Frankford Umbrellas Open Quote #${id.slice(-7)}`
      );
      NotificationService.show(
        `Legacy quote #${id.slice(-7)} downloaded.`,
        "success"
      );
    } catch (err) {
      if (err instanceof AuthError) {
        AuthError.logout();
      }
      GenericError.popup(err.message);
    }
  }

  protected async emailNetSuiteQuote(quoteId: number): Promise<void> {
    try {
      NotificationService.show(`Emailing quote #${quoteId}...`, "warning");
      await this.netSuiteService.sendQuoteEmail(quoteId);
      NotificationService.show(`Quote #${quoteId} email sent.`, "success");
    } catch (err) {
      if (err instanceof AuthError) {
        AuthError.logout();
      }
      GenericError.popup(err.message);
    }
  }

  protected async downloadNetSuiteQuote(
    quoteId: number,
    quoteNumber: number
  ): Promise<void> {
    try {
      NotificationService.show(
        `Downloading quote #${quoteNumber}...`,
        "warning"
      );
      const blobData = await this.netSuiteService.getQuotePdf(quoteId);
      this.forceDownload(blobData, `Frankford Umbrellas Quote #${quoteNumber}`);
      NotificationService.show(`Quote #${quoteNumber} downloaded.`, "success");
    } catch (err) {
      if (err instanceof AuthError) {
        AuthError.logout();
      }
      GenericError.popup(err.message);
    }
  }

  /**
   * FUPOF-191: the Sales Order PDF for a converted quote. Only rows carrying a
   * `netsuite_sales_order_id` (the Processed tab) can reach this.
   */
  protected async downloadNetSuiteSalesOrder(
    salesOrderId: number,
    salesOrderNumber?: string | null
  ): Promise<void> {
    const label = salesOrderNumber || `#${salesOrderId}`;
    try {
      NotificationService.show(
        `Downloading sales order ${label}...`,
        "warning"
      );
      const blobData = await this.netSuiteService.getSalesOrderPdf(
        salesOrderId
      );
      this.forceDownload(blobData, `Frankford Umbrellas Sales Order ${label}`);
      NotificationService.show(`Sales order ${label} downloaded.`, "success");
    } catch (err) {
      if (err instanceof AuthError) {
        AuthError.logout();
      }
      GenericError.popup(err.message);
    }
  }

  protected forceDownload(blobData: any, fileName: string): void {
    const url = window.URL.createObjectURL(blobData);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${fileName}.pdf`);
    document.body.appendChild(link);
    link.click();
  }

  protected close(): void {
    this.id = "";
    this.action = null;
    this.status = null;
    this.pendingOrder = null;
    this.pendingSalesOrder = null;
    this.showModal = false;
  }

  /**
   * Opens the clear-cart confirmation before an edit/duplicate replaces the
   * store (decision D6: warn, then replace). Skipped when the cart holds no
   * unsaved work — an empty cart, or the same saved draft already loaded.
   */
  protected requestQuoteAction(
    order: Order,
    action: "edit" | "dupe" | "reorder"
  ): void {
    this.pendingOrder = order;
    // FUPOF-200: this entry point is always quote-row driven, so make sure a
    // Sales Order left over from a previous SalesOrderList action can't be
    // picked up by confirm() in preference to the row actually clicked.
    this.pendingSalesOrder = null;
    this.action = action;
    this.id = order._id ?? "";
    this.status = order.status ?? null;
    const cartIsEmpty = !this.items || this.items.length === 0;
    const sameDraftLoaded =
      !!order.draft_id && this.mixinStoreOrder.draft_id === order.draft_id;
    if (cartIsEmpty || (action === "edit" && sameDraftLoaded)) {
      this.confirm();
      return;
    }
    this.showModal = true;
  }

  /**
   * A line added directly in NetSuite comes back with an empty top-level
   * product_line — the SKU parser records the real one on the reconstructed
   * layout instead. Fall back to it, otherwise ItemCustomization sees an empty
   * `handle` query and bounces the user to the collections menu.
   */
  protected resolveProductLineHandle(item: LineItem): string {
    if (item.product_line) {
      return item.product_line;
    }
    const details = item.item_details as any;
    return details?.layout?.productline?.handle ?? "";
  }

  /**
   * FUPOF-193: the note under a design member's qty stepper. The member's
   * stepper stays editable, but the umbrella's stepper resets every member to
   * its own qty (line_setup.syncSetupQty), so a different qty here does not
   * last. Said quietly while in step, and as a warning — with the umbrella's
   * qty and the consequence — once it isn't, so a rep who sets it on purpose
   * knows what will undo it. Null for every line that isn't a member.
   */
  protected setupQtyNote(
    index: number
  ): { text: string; warn: boolean } | null {
    const group = setupGroupAt(this.items, index);
    if (!group || group.anchor === index) return null;
    const umbrellaQty = this.items[group.anchor].qty;
    if (this.items[index].qty === umbrellaQty) {
      return { text: "Follows umbrella quantity", warn: false };
    }
    return {
      text: `Umbrella quantity is ${umbrellaQty} — changing the umbrella's quantity or updating its design will reset this line to match.`,
      warn: true
    };
  }

  /**
   * FUPOF-193: this line's part of its Shade Designer design has been
   * adjusted. Per line, not per setup: a canopy change reads on the umbrella
   * only, a base finish change on the base only. A part removed from the
   * design is flagged on the line it hung off (see
   * shared_design.designBaseMissing).
   */
  protected isDesignAdjusted(index: number): boolean {
    return sharedDesignAdjusted(this.items[index]);
  }

  /**
   * FUPOF-193: the cart pencil's tooltip and accessible name. An umbrella line
   * opens the page that edits its whole design — base and cover too, and it
   * can add a base where there is none — so it reads "Edit Eclipse Design"
   * whether or not a base is on the quote. Its base and cover open that same
   * page: "Edit Base in Eclipse Design", "Edit Top Cover in Eclipse Design".
   * Everything else is one line: "Edit Item".
   *
   * No loaded source maps a product-line handle to its display name (the
   * collections list is fetched only on the collections page), so the handle
   * is title-cased: "monterey_auto_tilt" reads "Monterey Auto Tilt".
   */
  protected editItemLabel(index: number): string {
    const item = this.items[index];
    if (!item) return "Edit Item";
    const group = setupGroupAt(this.items, index);
    if (group && group.anchor !== index) {
      // A member's pencil opens its umbrella's design at the Base card.
      const part =
        item.product_type === ProductType.Base ? "Base" : "Top Cover";
      return `Edit ${part} in ${this.designName(group.anchor)}`;
    }
    if (item.product_type !== ProductType.Umbrella) return "Edit Item";
    return `Edit ${this.designName(index)}`;
  }

  /** "Eclipse Design", from the umbrella line's product-line handle. */
  private designName(index: number): string {
    const handle = this.resolveProductLineHandle(this.items[index]);
    return handle
      ? `${makeTitleCase(handle.replace(/[_-]/g, " "))} Design`
      : "Design";
  }

  /**
   * FUPOF-193: whether a line gets the cart's edit pencil. A setup's base and
   * cover do whenever their umbrella could be opened itself — the pencil
   * edits them on the umbrella's page (see editItem). Otherwise: any line
   * with a configuration to reopen, bar accessories and NetSuite-locked lines.
   */
  protected showEditPencil(index: number): boolean {
    const group = setupGroupAt(this.items, index);
    const target = this.items[group ? group.anchor : index];
    if (!target || target.ns_locked || !target.item_details) return false;
    if (group && group.anchor !== index) return true;
    return target.product_type !== ProductType.Accessory;
  }

  protected editItem(idx?: number): void {
    const index = idx !== undefined ? idx : this.items.length - 1;
    const item = this.items[index];
    // FUPOF-193: a setup's base and cover are edited where the setup was
    // configured — on the umbrella page, which owns all three lines — landing
    // on its Base card. CustomizeBase knows nothing about the umbrella, so
    // editing a member there would let it drift from the setup it belongs to.
    // The cart's base and cover pencils come through here. A standalone base
    // (no setup, or one the render rule doesn't count as a member) still goes
    // to CustomizeBase below.
    const group = setupGroupAt(this.items, index);
    if (group && group.anchor !== index) {
      const anchorHandle = this.resolveProductLineHandle(
        this.items[group.anchor]
      );
      if (!anchorHandle) {
        GenericError.popup(
          "This line's umbrella has no product line on it and cannot be opened for editing. Its quantity, price and notes are still editable from the cart."
        );
        return;
      }
      this.$router.push({
        name: "ItemCustomization",
        query: {
          handle: anchorHandle,
          cart: group.anchor.toString(),
          section: "base"
        },
        params: {
          product: ProductType.Umbrella,
          edit: "true"
        }
      });
      return;
    }
    const indexAsString = index > 0 ? index.toString() : "0";
    const handle =
      item.product_type === this.productType.Base
        ? item.model
        : this.resolveProductLineHandle(item);
    if (!handle) {
      GenericError.popup(
        "This line has no product line on it and cannot be opened for editing. Its quantity, price and notes are still editable from the cart."
      );
      return;
    }
    this.$router.push({
      name: "ItemCustomization",
      query: { handle: handle, cart: indexAsString },
      params: {
        product: item.product_type,
        edit: "true"
      }
    });
  }

  // Triggered from child component, <Search />
  protected submitSearch(searchTerm: string): void {
    this.search = searchTerm;
  }

  /**
   * @param specifiedNetSuiteQuotes either the user's NetSuite quotes or the customer's NetSuite quotes
   * @description Converts the NetSuite quotes to the Order format
   * @output sets the NetSuite quotes for this mixin
   */
  protected convertNetSuiteQuotes(
    specifiedNetSuiteQuotes: NetSuiteQuote[]
  ): void {
    for (const quote of specifiedNetSuiteQuotes) {
      const convertedOrder: Order = ({
        _id: quote["document number"],
        netsuite_id: quote["quote id"],
        created_at: quote["date created"],
        customer_id: {
          customer_name: quote["customer name"],
          netsuite_id: quote["customer id"]
        },
        contact_details: {
          company_name: quote["customer name"],
          quote_name: quote["quote title"]
        },
        // items: [],
        items: quote.line_items ? quote.line_items : ([] as LineItem[]), // ToDo: FNSI-101 - convert NetSuiteQuoteLineItems to Order LineItems
        customer_netsuite_price_level_discount:
          quote.price_level_discount ?? undefined,
        netsuite_sales_order_id: quote["Sales Order ID"] ?? null,
        netsuite_sales_order_number: quote["Sales Order Number"] ?? null,
        netsuite_sales_order_date: quote["Sales Order Date"] ?? null,
        status: quote["status"],
        cost_details: {
          line_item_total: quote["total amt"]
        }
      } as unknown) as Order;
      // ToDo: FNSI-101 - write new function to convert the NetSuite quote line_items to the Order items format
      this.netSuiteQuotes.push(convertedOrder);
    }
  }

  // ToDo: FNSI-101 - further refactor this function as needed, if needed
  protected convertNetSuiteQuoteLineItemsToOrderLineItems(
    netSuiteQuoteLineItems: NetSuiteQuote[]
  ): LineItem[] {
    return netSuiteQuoteLineItems.map(item => ({
      // _id: item["line item id"],
      product_type: item["product type"],
      model: item["model"],
      product_line: item["product line"],
      qty: item["quantity"],
      unit_price: item["unit price"],
      multiplier: item["multiplier"] || 1,
      description: item["description"],
      display_name: item["display_name"] || item["model"] || "",
      notes: item["notes"] || ""
    }));
  }

  protected async getSingleNetSuiteQuoteLineItems(
    netSuiteQuote: NetSuiteQuote
  ): Promise<void> {
    try {
      const fullQuote = await this.netSuiteService.getNetSuiteQuoteLineItemsById(
        netSuiteQuote["quote id"]
      );
      console.log("fullQuote", fullQuote);
    } catch (err) {
      console.error("Error getting NetSuite quote line items", err);
    }
  }

  protected async getNetSuiteQuoteLineItems(
    netSuiteQuotes: NetSuiteQuote[]
  ): Promise<NetSuiteQuote[]> {
    if (!netSuiteQuotes.length) return netSuiteQuotes;

    await Promise.all(
      netSuiteQuotes.map(async quote => {
        try {
          quote.line_items = await this.netSuiteService.getNetSuiteQuoteLineItemsById(
            quote["quote id"]
          );
        } catch (err) {
          console.error(
            `Failed to fetch line items for quote ${quote["quote id"]}`,
            err
          );
          quote.line_items = [];
        }
      })
    );

    return netSuiteQuotes;
  }

  protected async createNetSuiteQuote(order: Order): Promise<any> {
    let draftId: number | null = null;
    try {
      // FUPOF-191: every submit goes through the PSQL draft, which owns line
      // identity — the API injects the NetSuite custom columns from PSQL, so
      // the payload carries no custcol_* of its own. Saving first persists the
      // lines; the API then forwards to NS (create, or update when the draft
      // already has a netsuite_quote_id) and flips the draft to submitted.
      const draft = await this.saveDraftAction();
      draftId = draft.id;
      const payload: CreateNetSuiteQuotePayload = await this.prepOrderToCreateNetSuiteQuote(
        order
      );
      const res = await this.quoteDraftService.submitQuote(draft.id, payload);
      if (res?.status !== "success") {
        throw new Error(
          res?.status
            ? `NetSuite returned status: ${res.status}`
            : "NetSuite did not return a success status."
        );
      }
      // The response carries the NS identity (netsuite_quote_id, and ns_line_id
      // per line) — merge it back rather than waiting for a reload to fill it in.
      if (res.quote) {
        await this.applySubmittedDraftAction(res.quote);
      }
      return res;
    } catch (error) {
      console.error("Error creating NetSuite quote:", error);
      const recovered = await this.recoverSubmittedDraft(draftId);
      if (recovered) return recovered;
      throw new Error(`Failed to create NetSuite quote. ${error.message}`);
    }
  }

  /**
   * Salvage a submit whose response never reached the browser.
   *
   * The submit holds one request open for as long as NetSuite takes to build the
   * estimate, and the gateway in front of the API gives up well before the API
   * does. When it does, the browser gets a 504 — but PHP does not stop: the
   * script keeps running, NetSuite finishes creating the estimate, and the API
   * still writes `netsuite_quote_id` and the per-line `ns_line_id`s to the draft
   * seconds later. The work succeeded; only the answer was lost.
   *
   * Left there, that is the worst state in the system. The rep is told the quote
   * failed while it exists in NetSuite, and the draft they resubmit or reopen is
   * the one whose missing link makes the edit flow adopt from NetSuite instead —
   * which locks the custom lines and empties the header (see `openQuoteForEdit`).
   * A second submit creates a second estimate.
   *
   * So on failure, re-read the draft a few times over the next ~45 seconds and
   * see whether the link appeared. If it did, the submit is complete and the
   * caller continues to the confirmation exactly as it would have. Polling is
   * backed off rather than tight: the API has already shown it is under load,
   * and this is the request that proves it.
   *
   * Returns a submit-shaped response on recovery, or null to let the original
   * error stand.
   */
  protected async recoverSubmittedDraft(
    draftId: number | null
  ): Promise<{ status: string; quote: QuoteDraft } | null> {
    if (draftId === null) return null;
    const waits = [3000, 5000, 8000, 12000, 17000];
    for (const wait of waits) {
      await new Promise(resolve => setTimeout(resolve, wait));
      let draft: QuoteDraft;
      try {
        draft = await this.quoteDraftService.getQuote(draftId);
      } catch (err) {
        // The read failing tells us nothing about the submit — keep waiting.
        continue;
      }
      if (draft?.netsuite_quote_id) {
        await this.applySubmittedDraftAction(draft);
        NotificationService.show(
          "Your quote reached NetSuite — the confirmation just took longer than expected to come back.",
          "success"
        );
        return { status: "success", quote: draft };
      }
    }
    return null;
  }

  // ToDo: FNSI-101 - 2025-06-02 - Test & further this function logic.
  protected async prepOrderToCreateNetSuiteQuote(
    order: Order
  ): Promise<CreateNetSuiteQuotePayload> {
    if (!order.customer_netsuite_id) {
      throw new Error("NetSuite customer ID is missing.");
    }
    const shipping = order.shipping_details;
    const shippingAddress: CreateNetSuiteQuoteShippingAddress = {
      ...(shipping?.address_id ? { address_id: shipping.address_id } : {}),
      addressee: shipping?.addressee ?? "",
      attention: shipping?.attention ?? "",
      addr1: shipping?.address?.address_1 ?? "",
      city: shipping?.address?.city ?? "",
      state: shipping?.address?.state ?? "",
      zip: shipping?.address?.postal_code ?? "",
      country: shipping?.address?.country ?? "US",
      is_residential: /Address Type - Residential\./.test(
        shipping?.freight_instructions ?? ""
      )
    };
    const contactDetails = order.contact_details;
    const selectedContactId =
      order.customer_netsuite_contact_id ?? contactDetails?.contact_id;
    // Notes travel as two separate fields (they are NOT composed into one for
    // submit): `quoteNotes` is the rep's own customer-facing text, which the API
    // writes as a printable "Notes" line on the quote PDF; `internalNotes` is the
    // derived multiplier-review warning, which the API writes only to the Legacy
    // Notes/Comments field and never prints. Keeping the warning out of `quoteNotes`
    // is what stops it reaching the customer. The warning is derived from the cart,
    // never persisted to `order.notes`.
    const customerNotes = stripQuoteNotes(order.notes);
    const internalNotes = multiplierWarning((order.items ?? []) as LineItem[]);
    const quotePayload: CreateNetSuiteQuotePayload = {
      action: "createQuote",
      title: contactDetails?.quote_name ?? "",
      salesrep: this.user.netsuite_id,
      customerId: order.customer_netsuite_id,
      shippingInstructions: /Freight Estimate - Yes\./.test(shipping?.freight_instructions ?? "")
        ? shipping!.freight_instructions!
        : "",
      requestShippingQuote: /Freight Estimate - Yes\./.test(shipping?.freight_instructions ?? ""),
      sendEmail: order.contact_details?.send_email ?? false,
      ...(customerNotes ? { quoteNotes: customerNotes } : {}),
      ...(internalNotes ? { internalNotes } : {}),
      ...(selectedContactId ? { contactId: selectedContactId } : {}),
      contact: {
        title: contactDetails?.title ?? null,
        firstname: contactDetails?.first_name ?? null,
        lastname: contactDetails?.last_name ?? null,
        phone: contactDetails?.phone ?? null,
        email: contactDetails?.email ?? ""
      },
      shippingAddress,
      items: [] as CreateNetSuiteQuoteLineItem[]
    };
    if (!order.items || order.items.length === 0) {
      throw new Error("No items provided to create a quote.");
    }
    if (!this.storePriceLevels?.length) {
      // Nothing to resolve a line's level against, so every line would silently
      // fall back to the customer's default and be quoted at the wrong price.
      // The list is loaded once at login and cached, so this is a genuine fault.
      await this.addPriceLevelsAction(
        await this.netSuiteService.getPriceLevels()
      );
    }
    try {
      // Check whether each SKU exists in NetSuite; unknown SKUs fall back to a
      // custom item. The lookups are independent per line, so fire them all at
      // once instead of one-by-one — getNetSuiteItemBySku swallows its own
      // errors (returns {}), so a single miss can't reject the batch. Promise.all
      // preserves order, so the results line up with order.items by index.
      const skus = order.items.map(item => item.sku || item.model || "");
      const netSuiteItems: FoundNetSuiteItemBySku[] = await Promise.all(
        skus.map(sku => this.netSuiteService.getNetSuiteItemBySku(sku))
      );
      quotePayload.items = order.items.map((item, index) => {
        const itemSku = skus[index];
        const netSuiteItem = netSuiteItems[index];
        const nsItemId = netSuiteItem?.id;
        const priceLevel = this.resolveLinePriceLevel(item, order);
        // FUPOF-191: spell the configuration out into the production notes only for
        // lines NetSuite has no item for — a resolved SKU already describes itself,
        // and the placeholder TEMP-CUST-ITEM line does not.
        //
        // Built from the store's line, not `item`: `order.items` is the clone
        // OrderReview.parseItems() produced, and that flattens canopy fabric swatches
        // to bare mfr codes, which buildLineDescription refuses to render. Both arrays
        // come from the same `state.items`, so they are index-aligned.
        const configSource =
          !nsItemId && this.items?.length === order.items?.length
            ? this.items[index]
            : null;
        return {
          customitem: nsItemId ? 0 : 1, // 0 = known item, 1 = custom item requiring VUE_APP_CUSTOM_NETSUITE_ITEM_ID
          itemId: nsItemId
            ? nsItemId
            : Number(process.env.VUE_APP_CUSTOM_NETSUITE_ITEM_ID),
          quantity: item.qty,
          description: netSuiteItem?.description
            ? netSuiteItem.description
            : `Custom ${item.model} - ${item.display_name}`,
          msrp: item.unit_price || 0.01,
          // Default price level ID of 1 is "Base Price". Never send a non-positive
          // id: NetSuite's built-in "Custom" level is -1, and a standard line sent at
          // Custom sources no rate at all, so the whole estimate fails to save with
          // "Please enter a value for Amount" (FUPOF-191).
          pricelevel: positivePriceLevelId(
            priceLevel?.id ?? order.customer_netsuite_price_level_id
          ),
          ...(priceLevel?.discountpct != null
            ? { pricelevel_discount: priceLevel.discountpct }
            : {}),
          customsku: itemSku,
          productionnotes: this.stringifyItemNotes(item, configSource)
        };
      });
      // Add surcharge percent to payload if env var is configured
      const surchargeVal = parseFloat(
        process.env.VUE_APP_PRICE_SURCHARGE_VALUE_NUMBER || ""
      );
      if (!isNaN(surchargeVal) && surchargeVal > 0) {
        quotePayload.surchargePercent = surchargeVal;
      }
    } catch (error) {
      console.error("Error preparing order for NetSuite quote:", error);
      throw new Error(
        `Failed to prepare order for NetSuite quote. ${error.message}`
      );
    }
    return quotePayload;
  }

  /**
   * FUPOF-191: the PriceLevel a submitted line should be quoted at.
   *
   * The quote multiplier the rep sets in `EditMultiplier` only ever lived in the
   * cart and the PSQL draft — the submit payload hard-coded the customer's
   * default level, so a quote moved to x0.35 was still priced at the customer's
   * x0.60 in NetSuite. Both `pricelevel` and `pricelevel_discount` are taken off
   * the resolved PriceLevel object here; neither is computed from the line's
   * multiplier, so the discount NetSuite applies is exactly the one NetSuite
   * published for that level.
   *
   * Resolved per line rather than once per quote: a line whose multiplier was
   * accepted from NetSuite during drift resolution (`ns_multiplier`) sits on its
   * own level, and the quote-wide choice must not silently re-price it. Matching
   * is on the multiplier value because that is all a line stores — via
   * `priceLevelMultiplier` on both sides so the comparison isn't fighting the
   * `1 + discountpct` float artifact.
   *
   * Falls back to the quote-wide level, then to the customer's default; a null
   * return leaves the caller on its pre-existing `customer_netsuite_price_level_id`
   * behavior and sends no discount. Levels NetSuite cannot price a line at (id <= 0)
   * are never returned — see the filter below.
   */
  protected resolveLinePriceLevel(
    item: LineItem,
    order: Order
  ): PriceLevel | null {
    // Non-positive ids are excluded before any matching: NetSuite's built-in "Custom"
    // level (id -1) carries a null discount, so priceLevelMultiplier() reads it as x1
    // and it out-matched the real x1 level for every full-MSRP line. A standard line
    // submitted at Custom sources no rate and fails the estimate save with "Please
    // enter a value for Amount" (FUPOF-191). `storePriceLevels` already filters these
    // out; repeated here so the resolver is correct on any list it is handed.
    const levels = (this.storePriceLevels ?? []).filter(
      level => typeof level.id === "number" && level.id > 0
    );
    if (item.multiplier != null) {
      const target = item.multiplier.toFixed(4);
      const match = levels.find(
        level => priceLevelMultiplier(level.discountpct).toFixed(4) === target
      );
      if (match) return match;
    }
    const quoteLevel = this.quoteMultiplierLevel;
    if (quoteLevel && typeof quoteLevel.id === "number" && quoteLevel.id > 0) {
      return quoteLevel;
    }
    const customerLevelId = order.customer_netsuite_price_level_id;
    return customerLevelId
      ? levels.find(level => level.id === customerLevelId) ?? null
      : null;
  }

  /* Note: FNSI-101 - Not in use yet */
  protected async getNetSuiteQuotes(): Promise<void> {
    const userNetSuiteQuotes: NetSuiteQuote[] = await this.netSuiteService.getMyQuotes();
    // Note: FNSI-101 - test block
    if (userNetSuiteQuotes.length > 0) {
      const fullQuote0 = await this.netSuiteService.getNetSuiteQuoteLineItemsById(
        userNetSuiteQuotes[0]["quote id"]
      );
      // console.log("fullQuote0", fullQuote0);
      const fullQuote1 = await this.netSuiteService.getNetSuiteQuoteLineItemsById(
        userNetSuiteQuotes[1]["quote id"]
      );
      // console.log("fullQuote", fullQuote1);
    }
    // Note: FNSI-101 - end test block
    /*
    // ToDo: FNSI-101 - move below block logic to appropriate location for whenever we're ready
    // to obtain the line items of the quotes. Confirming that the logic Works; however,
    // currently not needed right now in this location.

    if (userNetSuiteQuotes.length > 0) {
      const userFullNetSuiteQuotes: unknown[] = [];
      for (const quote of userNetSuiteQuotes) {
        const fullQuote = await this.netSuiteService.getNetSuiteQuoteLineItemsById(
          quote["quote id"]
        );
        if (fullQuote) {
          userFullNetSuiteQuotes.push(fullQuote);
        }
      }
      console.log("userFullNetSuiteQuotes", userFullNetSuiteQuotes);
    }
    */
  }
}
